import React from "react";
import styles from "./IconButton.module.css";

export type IconButtonVariant = "brandSolid" | "brandSubtle" | "neutral";

const variantStyles: Record<IconButtonVariant, React.CSSProperties> = {
  brandSolid: {
    backgroundColor: "var(--btn-bg, #a0a08f)",
    color: "var(--btn-text, #111827)",
    borderColor: "var(--btn-border, #8f8f7b)",
  },
  brandSubtle: {
    backgroundColor: "var(--btn-light-bg, #e1e1d2)",
    color: "var(--btn-text, #111827)",
    borderColor: "var(--btn-light-border, #c4c4b0)",
  },
  neutral: {
    backgroundColor: "white",
    color: "var(--color-text)",
    borderColor: "var(--color-border)",
  },
};

export interface IconButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  icon?: React.ReactNode;
  iconPosition?: "left" | "right";
  variant?: IconButtonVariant;
  /** Bezig-toestand: knop uitgeschakeld en het icoon vervangen door een spinner. */
  loading?: boolean;
}

export const IconButton: React.FC<IconButtonProps> = ({
  icon: eigenIcoon,
  iconPosition = "left",
  variant = "brandSolid",
  loading = false,
  children,
  style,
  disabled: eigenDisabled,
  ...props
}) => {
  const disabled = eigenDisabled || loading;
  const icon = loading ? (
    <span className={styles.spinner} aria-hidden="true" />
  ) : (
    eigenIcoon
  );
  const baseStyle: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.35rem",
    padding: "0.5rem 1rem",
    borderRadius: 9999,
    fontSize: "0.9rem",
    fontWeight: 600,
    borderWidth: 1,
    borderStyle: "solid",
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.65 : 1,
    ...variantStyles[variant],
    ...style,
  };

  const content =
    icon && iconPosition === "left" ? (
      <>
        {icon}
        {children}
      </>
    ) : icon && iconPosition === "right" ? (
      <>
        {children}
        {icon}
      </>
    ) : (
      children
    );

  return (
    <button
      style={baseStyle}
      disabled={disabled}
      aria-busy={loading || undefined}
      {...props}
    >
      {content}
    </button>
  );
};
